import { Chip, Tooltip } from "@mui/material";
import RefreshIcon from '@mui/icons-material/Refresh';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from "@fenetech/translations";
import { SaberisDocumentStatusEnum, ThemeColorEnum } from "helpers/enums";
import Format from "helpers/fv.format";
import useWait from "helpers/context/Page/useWait";
import { ISaberisDocumentStatus } from "helpers/interfaces";
import { SplitPascalCaseIntoWords } from "helpers/objects";

interface IProps {
    saberisDocument: ISaberisDocumentStatus,
    refreshSaberisDocument: () => Promise<void>
}

const SaberisChip: React.FC<IProps> = ({ saberisDocument, refreshSaberisDocument }: IProps) => {

    const tm = useTranslations();
    const wait = useWait();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        wait.Show(isLoading);
    }, [wait, isLoading]);

    const saberisDocumentStatusPhrase = useMemo(() => {
        if (saberisDocument.status === SaberisDocumentStatusEnum.ProcessComplete) {
            return SaberisDocumentStatusEnum.Completed;
        }
        if (saberisDocument.status === SaberisDocumentStatusEnum.ProblemReadingDocument || saberisDocument.status === SaberisDocumentStatusEnum.ErrorProcessing) {
            return SaberisDocumentStatusEnum.ErrorReadingDocument;
        }
        return saberisDocument.status;
    }, [saberisDocument]);

    const chipColor: ThemeColorEnum = useMemo(() => {
        switch(saberisDocument.status) {
            case SaberisDocumentStatusEnum.Completed: case SaberisDocumentStatusEnum.ProcessComplete:
                return ThemeColorEnum.Success;
            case SaberisDocumentStatusEnum.NotUploaded: case SaberisDocumentStatusEnum.RequestQueued:
                return ThemeColorEnum.Info;
            case SaberisDocumentStatusEnum.ProblemReadingDocument: case SaberisDocumentStatusEnum.ErrorReadingDocument: case SaberisDocumentStatusEnum.ErrorProcessing:
            case SaberisDocumentStatusEnum.Error: case SaberisDocumentStatusEnum.ModuleDisabled: 
                return ThemeColorEnum.Error
            default:
                return ThemeColorEnum.Info;
        }
    }, [saberisDocument]);

    const onRefreshStatus = useCallback(async () => {
        setIsLoading(true);
        try {
            await refreshSaberisDocument();
        }
        finally {
            setIsLoading(false);
        }
    }, [refreshSaberisDocument]);

    const tooltip = useMemo(() => {
        let tooltipText: string[] = [];

        if (saberisDocumentStatusPhrase !== SaberisDocumentStatusEnum.NotUploaded) {
            tooltipText.push(`${tm.Get("Date Uploaded:")} ${Format.FormatDate(saberisDocument.dateTimeAdded)}`);
            tooltipText.push(`${tm.Get("Last Updated:")} ${Format.FormatDate(saberisDocument.lastStatusCheckDateTime)}`);
            tooltipText.push(`${tm.Get("Uploaded By:")} ${saberisDocument.uploadedBy}`);
        }

        return tooltipText.join("\n");
    }, [saberisDocument, tm, saberisDocumentStatusPhrase]);

    const chipLabel = useMemo(() => {
        if (saberisDocumentStatusPhrase === SaberisDocumentStatusEnum.Completed || saberisDocumentStatusPhrase === SaberisDocumentStatusEnum.RequestQueued) {
            return <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tooltip}</span>}>
                <div>
                    {`${tm.Get("Saberis")}: ${tm.Get(SplitPascalCaseIntoWords(saberisDocumentStatusPhrase))}`}
                </div>
            </Tooltip>
        } else {
            return <div>
                {`${tm.Get("Saberis")}: ${tm.Get(SplitPascalCaseIntoWords(saberisDocumentStatusPhrase))}`}
            </div>
        }
    }, [tooltip, saberisDocumentStatusPhrase, tm]);

    const icon = useMemo(() => {
        if (saberisDocumentStatusPhrase === SaberisDocumentStatusEnum.Completed || saberisDocumentStatusPhrase === SaberisDocumentStatusEnum.RequestQueued) {
            return <Tooltip title={tm.Get("Refresh")}>
                <RefreshIcon />
            </Tooltip>
        } else {
            return <></>;
        }
    }, [saberisDocumentStatusPhrase, tm]);

    return <Chip
        label={chipLabel}
        color={chipColor}
        deleteIcon={icon}
        onDelete={onRefreshStatus}
    />
}

export default SaberisChip;